import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, CalendarDays, ChevronRight, Radio, Star } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import Layout from "@/components/Layout";
import { EventCard, LiveCard, EstablishmentCard, SectionHeader, CardRail } from "@/components/Cards";
import ResidenceCard from "@/components/residences/ResidenceCard";
import ResidenceSearchForm from "@/components/residences/ResidenceSearchForm";
import { ResidenceHowItWorks } from "@/components/residences/ResidenceHowItWorks";
import useResidenceMeta from "@/hooks/useResidenceMeta";
import { Button } from "@/components/ui/button";

const CATS = ["Bar", "Maquis", "Restaurant", "Club", "Lounge", "Concert", "Soirée"];
const WHENS = [["today", "Aujourd'hui"], ["tomorrow", "Demain"], ["weekend", "Ce week-end"], ["week", "Cette semaine"]];
const MapView = lazy(() => import("@/components/MapView"));
const DAYS = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

export default function Home() {
  useSEO({
    title: "Sorties, événements & LIVE à Abidjan",
    description: "BABIMIX — Trouvez où sortir ce soir à Abidjan : bars, maquis, restaurants, clubs, concerts, DJ, événements et LIVE en Côte d'Ivoire.",
    keywords: "sortir à Abidjan, bars Abidjan, maquis Abidjan, restaurants Abidjan, concerts Abidjan, événements Abidjan, clubs Abidjan, LIVE Abidjan, Côte d'Ivoire",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "BABIMIX",
      url: "https://babimix.com",
      description: "Sorties, événements, établissements et LIVE à Abidjan, Côte d'Ivoire.",
      potentialAction: { "@type": "SearchAction", target: "https://babimix.com/recherche?q={search_term_string}", "query-input": "required name=search_term_string" },
    },
  });
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [commune, setCommune] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [when, setWhen] = useState("today");
  const [tonight, setTonight] = useState([]);
  const [spotlight, setSpotlight] = useState([]);
  const [lives, setLives] = useState([]);
  const [venues, setVenues] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [dayIdx, setDayIdx] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
  const [weekEvents, setWeekEvents] = useState([]);
  const [newEvents, setNewEvents] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);
  const [sponsoredRes, setSponsoredRes] = useState([]);
  const resMeta = useResidenceMeta();

  useEffect(() => {
    api.get("/events/tonight").then((r) => setTonight(r.data));
    api.get("/establishments", { params: { limit: 50 } }).then((r) => setSpotlight(r.data.filter((e) => e.sponsored && ["bar", "maquis"].includes(e.category)).slice(0, 4)));
    api.get("/live").then((r) => setLives(r.data));
    api.get("/establishments", { params: { featured: true } }).then((r) => setVenues(r.data.slice(0, 4)));
    api.get("/reference").then((r) => { setCommunes(r.data.communes); setCities(r.data.cities || []); });
    api.get("/events", { params: { when: "week" } }).then((r) => setWeekEvents(r.data));
    api.get("/establishments", { params: { limit: 500 } }).then((r) => setMapPoints(r.data.filter((e) => e.lat && e.lng)));
    api.get("/events/new").then((r) => setNewEvents(r.data)).catch(() => {});
    api.get("/residences", { params: { featured: true, limit: 8 } }).then((r) => setSponsoredRes(r.data.items)).catch(() => {});
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    if (commune && (!city || city === "abidjan")) p.set("commune", commune);
    if (when) p.set("when", when);
    nav(`/recherche?${p.toString()}`);
  };

  const dayFor = (idx) => {
    const now = new Date();
    const diff = idx - (now.getDay() === 0 ? 6 : now.getDay() - 1);
    const d = new Date(now); d.setDate(now.getDate() + diff);
    return d.toISOString().slice(0, 10);
  };
  const mapH = typeof window !== "undefined" && window.innerWidth < 640 ? 320 : 460;
  const eventsOfDay = weekEvents.filter((e) => e.date === dayFor(dayIdx));

  return (
    <Layout>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src="/media/hero.jpg" alt="Nightlife Abidjan" fetchpriority="high" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 sm:pt-24 sm:pb-20">
          <div className="max-w-3xl fade-up">
            <span className="inline-block text-xs font-mono uppercase tracking-widest text-[#FF5A00] mb-3">Côte d'Ivoire · Abidjan</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-white">
              Les lieux. Les artistes.<br /><span className="text-flame">Les événements.</span>
            </h1>
            <p className="text-white/80 mt-3 sm:mt-4 text-sm sm:text-lg max-w-xl">Découvrez ce qui se passe autour de vous. Où sortir ce soir ? Quel artiste se produit ? Où y a-t-il un LIVE ?</p>

            {/* SEARCH ENGINE */}
            <div className="mt-6 sm:mt-8 glass rounded-2xl p-3 sm:p-4 border border-white/10">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="Que recherchez-vous ? (Bar, Concert, DJ...)"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 outline-none focus:border-[#FF5A00]/60" />
                </div>
                <Button data-testid="hero-search-btn" onClick={doSearch} className="flame-gradient rounded-xl font-bold px-8 py-3.5 h-auto text-base">RECHERCHER</Button>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3">
                <div className="relative">
                  <MapPin className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF5A00]" />
                  <select data-testid="hero-city-select" value={city} onChange={(e) => { setCity(e.target.value); if (e.target.value && e.target.value !== "abidjan") setCommune(""); }}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 sm:pl-11 pr-2 sm:pr-4 py-3 outline-none text-sm appearance-none truncate">
                    <option value="">Toutes les villes</option>
                    {cities.map((c) => <option key={c.slug} value={c.slug} className="bg-[#FFFFFF]">{c.name}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF5A00]" />
                  <select data-testid="hero-commune-select" value={commune} onChange={(e) => setCommune(e.target.value)} disabled={city && city !== "abidjan"}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 sm:pl-11 pr-2 sm:pr-4 py-3 outline-none text-sm appearance-none truncate disabled:opacity-40">
                    <option value="">Toutes communes</option>
                    {communes.map((c) => <option key={c.slug} value={c.slug} className="bg-[#FFFFFF]">{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                {WHENS.map(([v, l]) => (
                  <button key={v} data-testid={`when-${v}`} onClick={() => setWhen(v)}
                    className={`shrink-0 px-4 py-2.5 sm:py-3 rounded-xl text-sm font-semibold border ${when === v ? "flame-gradient border-transparent" : "border-white/10 bg-white/5 text-slate-300"}`}>{l}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                {CATS.map((c) => (
                  <button key={c} data-testid={`hero-cat-${c}`} onClick={() => { setQ(c); }} className="shrink-0 text-xs font-semibold px-3 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-[#FF5A00]/50">{c}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* À LA UNE — SPONSORISÉS */}
        {spotlight.length > 0 && (
          <section className="mt-12 sm:mt-16" data-testid="section-a-la-une">
            <div className="relative rounded-3xl p-5 sm:p-7 overflow-hidden" style={{ background: "#123B4A" }}>
              <div className="absolute -top-14 -right-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,90,79,0.4) 0%, transparent 70%)" }} />
              <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(56,181,216,0.3) 0%, transparent 70%)" }} />
              <div className="relative">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                  <div>
                    <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#FF5A4F]"><Star className="w-4 h-4 fill-[#FF5A4F]" /> À la une</span>
                    <h2 className="font-display text-2xl sm:text-3xl font-black text-white mt-1">Les bars & maquis du moment</h2>
                  </div>
                  <Link to="/etablissements" className="text-sm font-semibold text-[#FFC857] flex items-center shrink-0">Tout voir <ChevronRight className="w-4 h-4" /></Link>
                </div>
                <CardRail testId="rail-a-la-une">
                  {spotlight.map((e) => <EstablishmentCard key={e.id} est={e} />)}
                </CardRail>
              </div>
            </div>
          </section>
        )}

        <section className="mt-12 sm:mt-16 rounded-3xl overflow-hidden relative" data-testid="section-find-residence">
          <img src="/media/res-hero.jpg" alt="Résidence meublée" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#123B4A]/85 via-[#123B4A]/60 to-[#123B4A]/30" />
          <div className="relative p-5 sm:p-10">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#FFC857]">Résidences meublées</span>
            <h2 className="font-display text-2xl sm:text-4xl font-black text-white mt-1">Trouver une résidence</h2>
            <p className="text-white/85 text-sm sm:text-base mt-1 mb-4 sm:mb-6">Studios, appartements et villas meublés. Réservation gratuite, paiement directement avec le propriétaire.</p>
            <ResidenceSearchForm meta={resMeta} />
          </div>
        </section>
        <ResidenceHowItWorks />

        {sponsoredRes.length > 0 && (
          <section className="mt-12 sm:mt-16 rounded-3xl bg-gradient-to-br from-[#38B5D8]/15 via-white to-[#FFC857]/20 border border-[#EFE6DB] p-5 sm:p-8" data-testid="section-residences-sponsorisees">
            <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#FF5A4F]">Sponsorisé</span>
                <h2 className="font-display text-xl sm:text-3xl font-extrabold text-[#123B4A]">Résidences meublées à la une</h2>
              </div>
              <Link to="/residences/recherche?featured=1" data-testid="sponsored-res-see-all" className="shrink-0 text-sm font-semibold text-[#FF5A4F]">Tout voir</Link>
            </div>
            <CardRail testId="rail-residences-sponsorisees">
              {sponsoredRes.map((r) => <ResidenceCard key={r.id} r={r} />)}
            </CardRail>
          </section>
        )}

        {/* NOUVEAUTÉS DE LA SEMAINE */}
        {newEvents.length > 0 && (
          <section className="mt-12 sm:mt-16" data-testid="section-nouveautes">
            <div className="rounded-3xl p-5 sm:p-7 bg-gradient-to-br from-[#38B5D8]/12 to-[#FFC857]/12 border border-[#38B5D8]/25">
              <SectionHeader emoji="✨" title="Nouveautés de la semaine" action={<Link to="/evenements" className="text-sm font-semibold text-[#FF5A00] flex items-center">Tout voir <ChevronRight className="w-4 h-4" /></Link>} />
              <CardRail testId="rail-nouveautes" cols="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {newEvents.slice(0, 8).map((ev) => <EventCard key={ev.id} ev={ev} />)}
              </CardRail>
            </div>
          </section>
        )}

        {/* CE SOIR */}
        <section className="my-12 sm:my-16">
          <SectionHeader emoji="🔥" title="CE SOIR" action={<Link to="/evenements" className="text-sm font-semibold text-[#FF5A00] flex items-center">Tout voir <ChevronRight className="w-4 h-4" /></Link>} />
          {tonight.length === 0 ? <p className="text-slate-400">Aucun événement ce soir pour le moment.</p> : (
            <CardRail testId="rail-ce-soir" cols="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tonight.slice(0, 8).map((ev) => <EventCard key={ev.id} ev={ev} />)}
            </CardRail>
          )}
        </section>

        {/* PROGRAMME DE LA SEMAINE */}
        <section className="my-12 sm:my-16">
          <SectionHeader emoji="📅" title="Programme de la semaine" />
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
            {DAYS.map((d, i) => {
              const count = weekEvents.filter((e) => e.date === dayFor(i)).length;
              return (
                <button key={d} data-testid={`day-${d}`} onClick={() => setDayIdx(i)}
                  className={`shrink-0 w-16 sm:w-20 py-2.5 sm:py-3 rounded-2xl flex flex-col items-center gap-1 border ${dayIdx === i ? "flame-gradient border-transparent" : "border-white/10 bg-white/5"}`}>
                  <span className="font-display font-extrabold">{d}</span>
                  <span className={`text-[11px] font-semibold ${dayIdx === i ? "text-white/90" : "text-slate-400"}`}>{count} event{count > 1 ? "s" : ""}</span>
                </button>
              );
            })}
          </div>
          {eventsOfDay.length === 0 ? <p className="text-slate-400">Rien de prévu ce jour.</p> : (
            <CardRail testId="rail-semaine" cols="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {eventsOfDay.slice(0, 8).map((ev) => <EventCard key={ev.id} ev={ev} />)}
            </CardRail>
          )}
        </section>

        {/* LIVE */}
        {lives.length > 0 && (
          <section className="my-12 sm:my-16">
            <SectionHeader title={<span className="flex items-center gap-2"><Radio className="w-7 h-7 text-[#FF5A00]" /> LIVE en direct</span>} action={<Link to="/live" className="text-sm font-semibold text-[#FF5A00] flex items-center">Tout voir <ChevronRight className="w-4 h-4" /></Link>} />
            <CardRail testId="rail-live" cols="sm:grid-cols-2 lg:grid-cols-3">
              {lives.map((l) => <LiveCard key={l.id} live={l} />)}
            </CardRail>
          </section>
        )}

        {/* CARTE CÔTE D'IVOIRE */}
        <section className="my-10 sm:my-16" data-testid="section-carte">
          <SectionHeader emoji="🗺️" title="Les établissements sur la carte" action={<Link to="/explorer" className="text-sm font-semibold text-[#FF5A00] flex items-center">Explorer <ChevronRight className="w-4 h-4" /></Link>} />
          <Suspense fallback={<div className="w-full rounded-2xl bg-white/5" style={{ height: mapH }} />}>
            <MapView points={mapPoints} center={[7.54, -5.55]} zoom={mapH < 460 ? 6 : 7} fit={false} height={mapH} />
          </Suspense>
          <p className="text-xs text-[#71828A] mt-2">{mapPoints.length} établissement{mapPoints.length > 1 ? "s" : ""} géolocalisé{mapPoints.length > 1 ? "s" : ""} en Côte d'Ivoire · cliquez sur un repère pour voir la fiche.</p>
        </section>

        {/* ETABLISSEMENTS EN AVANT */}
        {venues.length > 0 && (
          <section className="my-12 sm:my-16">
            <SectionHeader emoji="🍻" title="Établissements en vedette" action={<Link to="/etablissements" className="text-sm font-semibold text-[#FF5A00] flex items-center">Tout voir <ChevronRight className="w-4 h-4" /></Link>} />
            <CardRail testId="rail-vedette">
              {venues.map((e) => <EstablishmentCard key={e.id} est={e} />)}
            </CardRail>
          </section>
        )}

        {/* BUSINESS CTA */}
        <section className="mt-12 mb-6 sm:my-16 relative rounded-3xl overflow-hidden" style={{ background: "#123B4A" }}>
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(56,181,216,0.35) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,90,79,0.25) 0%, transparent 70%)" }} />
          <div className="relative p-6 sm:p-14 grid lg:grid-cols-[1.5fr_1fr] gap-6 items-center">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#FFC857] mb-3">BABIMIX Business</span>
              <h2 className="font-display text-2xl sm:text-4xl font-black text-white">Votre établissement mérite d'être vu.</h2>
              <p className="text-white/80 mt-3 max-w-xl">Rejoignez BABIMIX et faites connaître vos programmes, vos artistes et vos événements auprès de milliers de visiteurs.</p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <Button data-testid="home-business-cta" onClick={() => nav("/business")} className="flame-gradient hover:opacity-90 rounded-full font-bold py-6 text-base">Créer ma page BABIMIX Business</Button>
              <Button onClick={() => nav("/tarifs")} variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full font-bold py-6 bg-transparent">Voir les abonnements</Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
